import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService, LoginRequest } from '../services/authService';
import { useUser } from '../contexts/UserContext';
import AuthLayout from '../components/public/AuthLayout';
import TextField from '../components/public/TextField';
import DevDemoLogins from '../components/auth/DevDemoLogins';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { reloadUser } = useUser();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState(searchParams.get('password') ?? '');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [needs2FA, setNeeds2FA] = useState(false);
  const [pendingId, setPendingId] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  useEffect(() => {
    // Pre-fill from query params kept in state above; nothing else to do.
  }, [searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const payload: LoginRequest = { email, password };
      const response = await authService.login(payload);
      if ('requires_2fa' in response && response.requires_2fa) {
        setNeeds2FA(true);
        setPendingId(response.pending_id);
        setIsLoading(false);
        return;
      }
      await reloadUser();
      navigate('/app');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string } )
        .response?.data?.detail
        ?? (err as { message?: string }).message
        ?? 'Sign-in failed';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await authService.verify2FA({ pending_id: pendingId, code: twoFactorCode });
      await reloadUser();
      navigate('/app');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } }; message?: string } )
        .response?.data?.detail
        ?? (err as { message?: string }).message
        ?? 'Verification failed';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const primaryButton =
    'w-full bg-[var(--primary)] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[var(--primary-light)] disabled:opacity-50 transition-colors shadow-sm';

  if (needs2FA) {
    return (
      <AuthLayout
        title="Two-factor sign-in"
        subtitle="Enter the 6-digit code we just sent to your phone."
        footer={
          <button
            type="button"
            onClick={() => { setNeeds2FA(false); setTwoFactorCode(''); setError(null); }}
            className="text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
          >
            ← Back to sign in
          </button>
        }
      >
        <form onSubmit={handle2FA} className="space-y-5">
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </div>
          )}
          <TextField
            label="Verification code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoFocus
            required
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value)}
          />
          <button type="submit" disabled={isLoading || !twoFactorCode} className={primaryButton}>
            {isLoading ? 'Verifying…' : 'Verify'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your TravelMinds marketing portal."
      backHref="/"
      backLabel="Back to home"
      footer={
        <>
          <Link to="/forgot-password" className="block text-[var(--accent)] hover:underline font-medium">
            Forgot your password?
          </Link>
          <Link to="/signup" className="block text-[var(--text-muted)] hover:text-[var(--text)]">
            New here? Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={handleLogin} className="space-y-5">
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            {error}
          </div>
        )}
        <TextField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div>
          <div className="flex items-end justify-between mb-1.5">
            <label htmlFor="password" className="block text-sm font-medium text-[var(--text)]">Password</label>
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text)]"
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-[var(--border)] rounded-lg text-sm focus:outline-none focus:border-[var(--accent)] focus:ring-4 focus:ring-[var(--accent)]/15 transition-all"
          />
        </div>
        <button
          type="submit"
          disabled={isLoading || !email.trim() || !password.trim()}
          className={primaryButton}
        >
          {isLoading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <DevDemoLogins />
    </AuthLayout>
  );
};

export default Login;
